import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useWeekNavigation } from '../hooks/useWeekNavigation';
import { getWeekDays, parseISODate, isDateInMonth, getCalendarWeeksForMonth, formatWeekLabel, formatISODate, addWeeks } from '../utils/dateUtils';
import type { BankHoliday, ISODateString, Asset, Project, TimeEntry, NewTimeEntry, Person } from '../types/timesheet.types';
import { toISODateString, TimesheetStatus, ProjectType } from '../types/timesheet.types';
import { useConnector } from '../contexts/ConnectorContext';
import { WorkflowTriggerType } from '../types/connector.types';
import { SP_LISTS, VALIDATION, SOFT_LAUNCH } from '../utils/constants';
import { hhmmToDecimal, decimalToHHMM } from '../utils/calculations';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DayHeader {
  day: string;
  date: string;
  isWeekend: boolean;
  isoDate: ISODateString;
}

// ─── AssetHoursCell ───────────────────────────────────────────────────────────

interface AssetHoursCellProps {
  code: string;
  isoDate: ISODateString;
  hVal: number | null;
  isSubmitted: boolean;
  isWknd: boolean;
  onUpdate: (code: string, date: ISODateString, val: string) => void;
}

function AssetHoursCell({ code, isoDate, hVal, isSubmitted, isWknd, onUpdate }: AssetHoursCellProps) {
  const [rawVal, setRawVal] = useState<string | null>(null);

  const committedDisplay = hVal === null ? '' : decimalToHHMM(hVal);
  const displayVal = rawVal !== null ? rawVal : committedDisplay;
  const isEmpty = rawVal !== null ? rawVal === '' : hVal === null;

  const doStep = (dir: 1 | -1) => {
    const cur = rawVal !== null ? rawVal : committedDisplay;
    const base = cur === '' ? 0 : (isNaN(hhmmToDecimal(cur)) ? 0 : hhmmToDecimal(cur));
    const next = dir === 1
      ? Math.min(VALIDATION.MAX_DAILY_ASSET_HOURS, base + 0.25)
      : Math.max(0, base - 0.25);
    const hhmm = next === 0 ? '' : decimalToHHMM(next);
    setRawVal(hhmm);
    onUpdate(code, isoDate, hhmm);
  };

  return (
    <div className="hr-input-wrap" style={isWknd ? { opacity: 0.4 } : undefined}>
      <input
        className={`hr-input${isEmpty ? ' empty' : ''}${isWknd ? ' wknd' : ''}`}
        type="text"
        inputMode="decimal"
        placeholder="–"
        value={displayVal}
        readOnly={isSubmitted}
        onChange={isSubmitted ? undefined : e => setRawVal(e.target.value)}
        onKeyDown={isSubmitted ? undefined : e => {
          if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
          e.preventDefault();
          doStep(e.key === 'ArrowUp' ? 1 : -1);
        }}
        onBlur={isSubmitted ? undefined : () => {
          if (rawVal !== null) onUpdate(code, isoDate, rawVal);
          setRawVal(null);
        }}
      />
      {!isSubmitted && (
        <div className="hr-spin-btns">
          <button className="hr-spin-btn" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => doStep(1)} />
          <button className="hr-spin-btn" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => doStep(-1)} />
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildDayHeaders(weekDays: ISODateString[]): DayHeader[] {
  const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return names.map((day, i) => {
    const isoDate = weekDays[i];
    const d = parseISODate(isoDate);
    const dd = String(d.getDate()).padStart(2, '0');
    const mon = d.toLocaleDateString('en-GB', { month: 'short' });
    return { day, date: `${dd} ${mon}`, isWeekend: i >= 5, isoDate };
  });
}

function fmtHours(n: number): string {
  if (n <= 0) return '–';
  return decimalToHHMM(n);
}

function cellKey(projectCode: string, date: ISODateString): string {
  return `${projectCode}::${date}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AssetTimesheets() {
  const { weekLabel, monthLabel, goToPrevWeek, goToNextWeek, goToPrevMonth, goToNextMonth, goToWeek, weekStart, selectedMonth: periodMonth, calendarMonth } = useWeekNavigation();
  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart]);
  const dayHeaders = useMemo(() => buildDayHeaders(weekDays), [weekDays]);
  const monthWeeks = useMemo(() => getCalendarWeeksForMonth(periodMonth), [periodMonth]);
  const isFirstWeekOfMonth = monthWeeks.length > 0 && weekStart === monthWeeks[0].weekStart;
  const isLastWeekOfMonth  = monthWeeks.length > 0 && weekStart === monthWeeks[monthWeeks.length - 1].weekStart;
  const connector = useConnector();

  // ── Reference data ──────────────────────────────────────────────────────────
  const [assets, setAssets] = useState<Asset[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentUser, setCurrentUser] = useState<Person | null>(null);
  const [bankHolidays, setBankHolidays] = useState<BankHoliday[]>([]);

  // ── Timesheet state ─────────────────────────────────────────────────────────
  const [selectedAssetId, setSelectedAssetId] = useState<string>('');
  // '' in rowCodes = pending project selection row
  const [rowCodes, setRowCodes] = useState<string[]>([]);
  const [existingEntries, setExistingEntries] = useState<Map<string, TimeEntry>>(new Map());
  const [dirtyValues, setDirtyValues] = useState<Map<string, number | null>>(new Map());
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [overriddenBHDates, setOverriddenBHDates] = useState<Set<string>>(new Set());

  // ── Per-month code tracking (mirrors MyTimesheet.tsx monthAddedCodes) ───────
  // Persists project row codes per month so week navigation doesn't lose them.
  const monthRowCodes = useRef<Map<string, string[]>>(new Map());
  // Tracks which weekStart values we've already attempted rolling week for.
  const rolledOverWeeks = useRef<Set<ISODateString>>(new Set());
  // Persists unsaved dirty values per month so month navigation doesn't lose edits.
  const monthDirtyValues = useRef<Map<string, Map<string, number | null>>>(new Map());

  // ── UI state ────────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [showWeekPicker, setShowWeekPicker]         = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm]   = useState(false);
  const [pendingRemoveCode, setPendingRemoveCode]   = useState<string | null>(null);
  const [pendingBHRestoreDate, setPendingBHRestoreDate] = useState<ISODateString | null>(null);

  // ── Load reference data (assets, projects, current user) once ─────────────
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    Promise.all([
      connector.getAssets(),
      connector.getCurrentUser(),
      connector.getAssetPermittedMembers(),
    ])
      .then(async ([fetchedAssets, user, permittedEmails]) => {
        if (cancelled) return;
        const fetchedProjects = await connector.getProjectsForUser(user.m365Email);
        if (cancelled) return;
        // Members of the Asset Timesheet Permitted Group can fill in any
        // asset's timesheet (covers owners who are absent). Otherwise, a
        // user only sees assets where they are the responsible owner.
        const userEmail = user.m365Email.trim().toLowerCase();
        const isPermitted = permittedEmails.has(userEmail);
        const active = fetchedAssets.filter(a => {
          if (!a.active) return false;
          if (isPermitted) return true;
          return a.responsiblePeopleEmails.some(e => e.trim().toLowerCase() === userEmail);
        });
        setAssets(active);
        setProjects(fetchedProjects);
        setCurrentUser(user);
        if (active.length > 0) {
          setSelectedAssetId(prev => prev || active[0].id);
        } else {
          setIsLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load data');
          setIsLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [connector]);

  // ── Load bank holidays when year changes ───────────────────────────────────
  useEffect(() => {
    const year = parseInt(periodMonth.split('-')[0]);
    connector.getBankHolidays(year)
      .then(setBankHolidays)
      .catch(() => setBankHolidays([]));
  }, [connector, periodMonth]);

  // ── Reset per-month tracking when asset changes ────────────────────────────
  useEffect(() => {
    monthRowCodes.current = new Map();
    monthDirtyValues.current = new Map();
    rolledOverWeeks.current = new Set();
  }, [selectedAssetId]);

  // ── Restore month codes and dirty values on month change ──────────────────
  // Dirty values are restored from the ref; loadEntries will also restore them
  // after a fresh fetch, so we don't need to wipe them here.
  useEffect(() => {
    rolledOverWeeks.current = new Set();
    setRowCodes(monthRowCodes.current.get(calendarMonth) ?? []);
    setDirtyValues(monthDirtyValues.current.get(calendarMonth) ?? new Map());
  }, [calendarMonth, selectedAssetId]);

  // ── Reset BH overrides when week or asset changes ─────────────────────────
  useEffect(() => {
    setOverriddenBHDates(new Set());
  }, [weekStart, selectedAssetId]);

  // ── Load ALL month entries for the current month + selected asset ───────────
  // Does NOT depend on weekDays — week navigation no longer triggers a reload.
  const loadEntries = useCallback(
    async (assetId: string, user: Person, assetList: Asset[]) => {
      const asset = assetList.find(a => a.id === assetId);
      if (!asset) return;

      setIsLoading(true);
      setLoadError(null);

      try {
        const monthRaw = await connector.getTimeEntries({
          personEmail: user.m365Email,
          periodMonth,
        }, SP_LISTS.ASSET_TIMESHEETS);

        const assetEntries = monthRaw.filter(e => e.assetCode === asset.title);

        const map = new Map<string, TimeEntry>();
        const spCodes = new Set<string>();
        for (const e of assetEntries) {
          if (e.projectCode) {
            map.set(cellKey(e.projectCode, e.dateWorked), e);
            spCodes.add(e.projectCode);
          }
        }

        setExistingEntries(map);
        // Restore cached dirty values for this month (preserves unsaved edits across month nav).
        setDirtyValues(monthDirtyValues.current.get(calendarMonth) ?? new Map());

        // Only set row codes if we don't already have user-managed codes for this month.
        // This preserves any codes the user added or removed during the session.
        if (!monthRowCodes.current.has(calendarMonth)) {
          const initialCodes = [...spCodes];
          monthRowCodes.current.set(calendarMonth, initialCodes);
          setRowCodes(initialCodes);
        }

        // Submission status is month-scoped: all month entries must be in a terminal state.
        const allSubmitted =
          assetEntries.length > 0 &&
          assetEntries.every(
            e =>
              e.status === TimesheetStatus.Submitted ||
              e.status === TimesheetStatus.ManagerApproved ||
              e.status === TimesheetStatus.SuperApproved ||
              e.status === TimesheetStatus.Locked,
          );
        setIsSubmitted(allSubmitted);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load entries');
      } finally {
        setIsLoading(false);
      }
    },
    [connector, periodMonth, calendarMonth],
  );

  useEffect(() => {
    if (currentUser && selectedAssetId && assets.length > 0) {
      void loadEntries(selectedAssetId, currentUser, assets);
    }
  }, [loadEntries, currentUser, selectedAssetId, assets]);

  // ── Rolling week: pre-load previous week's project codes when month is empty ─
  // Runs separately from loadEntries so week navigation never triggers a reload.
  useEffect(() => {
    if (isLoading) return;
    if (!currentUser || !selectedAssetId || assets.length === 0) return;
    if (rolledOverWeeks.current.has(weekStart)) return;

    const asset = assets.find(a => a.id === selectedAssetId);
    if (!asset) return;

    // Skip roll-over if user already has codes for this month.
    if ((monthRowCodes.current.get(calendarMonth) ?? []).length > 0) {
      rolledOverWeeks.current.add(weekStart);
      return;
    }

    let cancelled = false;
    const prevWeekDays = getWeekDays(addWeeks(weekStart, -1));
    connector.getTimeEntries(
      { personEmail: currentUser.m365Email, dateFrom: prevWeekDays[0], dateTo: prevWeekDays[6] },
      SP_LISTS.ASSET_TIMESHEETS,
    )
      .then(prevRaw => {
        if (cancelled) return;
        const prevCodes = new Set<string>();
        for (const e of prevRaw) {
          if (e.assetCode === asset.title && e.projectCode && e.hours > 0) {
            prevCodes.add(e.projectCode);
          }
        }
        const codes = [...prevCodes];
        rolledOverWeeks.current.add(weekStart);
        setRowCodes(prev => {
          const pending = prev.filter(c => !c);
          const merged = [...new Set([...pending, ...codes])];
          monthRowCodes.current.set(calendarMonth, merged.filter(Boolean));
          return merged;
        });
      })
      .catch(() => { rolledOverWeeks.current.add(weekStart); });

    return () => { cancelled = true; };
  }, [connector, currentUser, selectedAssetId, assets, weekStart, calendarMonth, isLoading]);

  // ── Derived data ───────────────────────────────────────────────────────────

  const bankHolidayDates = useMemo(
    () => new Set(bankHolidays.map(bh => bh.holidayDate as string)),
    [bankHolidays],
  );

  const weekBankHolidays = useMemo(
    () => bankHolidays.filter(bh => (weekDays as string[]).includes(bh.holidayDate)),
    [bankHolidays, weekDays],
  );

  const selectedAsset = assets.find(a => a.id === selectedAssetId);
  const assetLabel = selectedAsset?.title ?? 'No asset selected';

  const projectMap = useMemo(() => {
    const m = new Map<string, Project>();
    for (const p of projects) m.set(p.projectCode, p);
    return m;
  }, [projects]);

  const usedCodes = useMemo(() => new Set(rowCodes.filter(Boolean)), [rowCodes]);
  const availableProjects = useMemo(
    () => projects
      .filter(p => !usedCodes.has(p.projectCode))
      .filter(p => p.projectCode.toUpperCase().startsWith('P'))
      .sort((a, b) => a.projectCode.localeCompare(b.projectCode)),
    [projects, usedCodes],
  );

  // ── Cell value helpers ─────────────────────────────────────────────────────

  const getCellHours = useCallback(
    (projectCode: string, date: ISODateString): number | null => {
      const key = cellKey(projectCode, date);
      const dirty = dirtyValues.get(key);
      if (dirty !== undefined) return dirty === 0 ? null : dirty;
      const hours = existingEntries.get(key)?.hours ?? null;
      return hours === 0 ? null : hours;
    },
    [dirtyValues, existingEntries],
  );

  const updateHours = useCallback(
    (projectCode: string, date: ISODateString, val: string) => {
      if (val === '') {
        setDirtyValues(prev => { const next = new Map(prev); next.set(cellKey(projectCode, date), null); monthDirtyValues.current.set(calendarMonth, next); return next; });
        return;
      }
      const parsed = hhmmToDecimal(val);
      if (isNaN(parsed) || parsed < 0 || parsed > VALIDATION.MAX_DAILY_ASSET_HOURS) return;
      setDirtyValues(prev => {
        const next = new Map(prev);
        next.set(cellKey(projectCode, date), parsed);
        monthDirtyValues.current.set(calendarMonth, next);
        return next;
      });
    },
    [calendarMonth],
  );

  // ── Row total ──────────────────────────────────────────────────────────────

  const rowTotal = useCallback(
    (projectCode: string): number =>
      dayHeaders.reduce((s, h) => s + (getCellHours(projectCode, h.isoDate) ?? 0), 0),
    [dayHeaders, getCellHours],
  );

  // ── Footer totals ──────────────────────────────────────────────────────────

  const dailyTotals = useMemo(
    () =>
      dayHeaders.map(h =>
        rowCodes
          .filter(Boolean)
          .reduce((s, code) => s + (getCellHours(code, h.isoDate) ?? 0), 0),
      ),
    [dayHeaders, rowCodes, getCellHours],
  );

  const bhTotal = weekBankHolidays.reduce((s, bh) => {
    const projHours = rowCodes.filter(Boolean).reduce((ps, code) => ps + (getCellHours(code, bh.holidayDate) ?? 0), 0);
    return s + Math.max(0, bh.defaultHours - projHours);
  }, 0);
  const grandTotal = dailyTotals.reduce((s, v) => s + v, 0) + bhTotal;

  // ── Save draft ─────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!currentUser || !selectedAsset || dirtyValues.size === 0) return;

    setIsSaving(true);
    setSaveError(null);

    const toSave: (TimeEntry | NewTimeEntry)[] = [];

    for (const [key, hours] of dirtyValues.entries()) {
      const [projectCode, date] = key.split('::') as [string, string];
      const isoDate = toISODateString(date);
      const existing = existingEntries.get(key);

      if (existing) {
        toSave.push({
          ...existing,
          hours: hours ?? 0,
          managerName: existing.managerName || currentUser.lineManager,
          managerEmail: existing.managerEmail || currentUser.lineManagerEmail,
          superApproverName: existing.superApproverName || currentUser.superApprover,
          superApproverEmail: existing.superApproverEmail || currentUser.superApproverEmail,
        });
      } else if (hours !== null && hours > 0) {
        const newEntry: NewTimeEntry = {
          id: `new-${crypto.randomUUID()}` as `new-${string}`,
          personEmail: currentUser.m365Email,
          personName: currentUser.fullName,
          projectCode,
          dateWorked: isoDate,
          hours,
          status: TimesheetStatus.Draft,
          managerName: currentUser.lineManager,
          managerEmail: currentUser.lineManagerEmail,
          managerId: null,
          managerApprovedDate: null,
          superApproverName: currentUser.superApprover,
          superApproverEmail: currentUser.superApproverEmail,
          superApproverId: null,
          superApprovedDate: null,
          submittedDate: null,
          lockedDate: null,
          unlockedDate: null,
          unlockReason: '',
          unlockApprovedBy: '',
          rejectionNote: '',
          billRate: null,
          costRate: null,
          taskDescription: '',
          billableToClient: false,
          assetCode: selectedAsset.title,
        };
        toSave.push(newEntry);
      }
    }

    try {
      if (toSave.length > 0) {
        const result = await connector.batchSaveEntries(toSave, SP_LISTS.ASSET_TIMESHEETS);
        if (result.failed.length > 0) {
          setSaveError(`${result.failed.length} entr${result.failed.length === 1 ? 'y' : 'ies'} failed to save`);
        }
      }
      setLastSaved(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
      // Clear cached dirty values — entries are now saved, reload will reflect server state.
      monthDirtyValues.current.delete(calendarMonth);
      await loadEntries(selectedAssetId, currentUser, assets);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  }, [connector, currentUser, selectedAsset, selectedAssetId, assets, dirtyValues, existingEntries, loadEntries, calendarMonth]);

  // Auto-save every minute when there are unsaved changes in Draft or Rejected status.
  // Only active while this component is mounted (user is on this screen).
  const latestHandleSave = useRef(handleSave);
  useEffect(() => { latestHandleSave.current = handleSave; }, [handleSave]);
  useEffect(() => {
    const timer = setInterval(() => {
      if (dirtyValues.size === 0 || isSaving || isSubmitted || !currentUser || !selectedAsset) return;
      // !isSubmitted covers draft + rejected — both are editable states
      void latestHandleSave.current();
    }, 60_000);
    return () => clearInterval(timer);
  }, [dirtyValues, isSaving, isSubmitted, currentUser, selectedAsset]);

  // ── Submit for approval ────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    if (!currentUser || !selectedAsset) return;
    if (dirtyValues.size > 0) await handleSave();
    setIsSaving(true);
    try {
      // Load all month entries for this asset, then update drafts to Submitted
      // with approver fields stamped — do not rely on workflow to set these.
      const monthEntries = await connector.getTimeEntries(
        { personEmail: currentUser.m365Email, periodMonth },
        SP_LISTS.ASSET_TIMESHEETS,
      );
      const submittedDate = formatISODate(new Date());
      const toSubmit = monthEntries
        .filter(e => e.assetCode === selectedAsset.title && (e.status === TimesheetStatus.Draft || e.status === TimesheetStatus.Rejected))
        .map(e => ({
          ...e,
          status: TimesheetStatus.Submitted,
          submittedDate,
          managerName: e.managerName || currentUser.lineManager,
          managerEmail: e.managerEmail || currentUser.lineManagerEmail,
          superApproverName: e.superApproverName || currentUser.superApprover,
          superApproverEmail: e.superApproverEmail || currentUser.superApproverEmail,
        }));
      if (toSubmit.length > 0) {
        await connector.batchSaveEntries(toSubmit, SP_LISTS.ASSET_TIMESHEETS);
      }
      const totalHours = toSubmit.reduce((s, e) => s + e.hours, 0);
      await connector.triggerWorkflow({
        triggerType: WorkflowTriggerType.SubmitMonth,
        personId: currentUser.id,
        userName: currentUser.fullName,
        userEmail: currentUser.m365Email,
        periodMonth,
        actionStatus: 'Submitted',
        isFinalState: false,
        approverName: SOFT_LAUNCH.enabled ? SOFT_LAUNCH.approverName : currentUser.lineManager,
        approverEmail: SOFT_LAUNCH.enabled ? SOFT_LAUNCH.approverEmail : currentUser.lineManagerEmail,
        approverRole: SOFT_LAUNCH.enabled ? 'Super Approver' : 'Manager',
        source: 'AssetTimesheet',
        totalHours,
      });
      setIsSubmitted(true);
      setLastSaved(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Submit failed');
    } finally {
      setIsSaving(false);
    }
  }, [connector, currentUser, selectedAsset, dirtyValues, handleSave, periodMonth]);

  // ── Confirm remove row ─────────────────────────────────────────────────────

  const confirmRemoveRow = useCallback((code: string) => {
    const idsToDelete = [...existingEntries.values()]
      .filter(e => e.projectCode === code)
      .map(e => e.id);
    if (idsToDelete.length > 0) {
      void connector.deleteTimeEntries(idsToDelete, SP_LISTS.ASSET_TIMESHEETS);
      setExistingEntries(prev => {
        const next = new Map(prev);
        for (const [k, e] of prev) {
          if (e.projectCode === code) next.delete(k);
        }
        return next;
      });
    }
    setDirtyValues(prev => {
      const next = new Map(prev);
      for (const k of prev.keys()) {
        if (k.startsWith(`${code}::`)) next.delete(k);
      }
      monthDirtyValues.current.set(calendarMonth, next);
      return next;
    });
    setRowCodes(prev => {
      const next = prev.filter(c => c !== code);
      monthRowCodes.current.set(calendarMonth, next.filter(Boolean));
      return next;
    });
    setPendingRemoveCode(null);
  }, [connector, existingEntries, calendarMonth]);

  const confirmBHRestore = useCallback((date: ISODateString) => {
    // Clear dirty values for all project rows on this BH date
    setDirtyValues(prev => {
      const next = new Map(prev);
      rowCodes.filter(Boolean).forEach(code => {
        next.set(cellKey(code, date), null);
      });
      monthDirtyValues.current.set(calendarMonth, next);
      return next;
    });
    // Delete persisted SP entries for this BH date
    const idsToDelete = [...existingEntries.entries()]
      .filter(([k]) => k.endsWith(`::${date}`))
      .map(([, e]) => e.id);
    if (idsToDelete.length > 0) {
      void connector.deleteTimeEntries(idsToDelete, SP_LISTS.ASSET_TIMESHEETS);
      setExistingEntries(prev => {
        const next = new Map(prev);
        for (const [k] of prev) {
          if (k.endsWith(`::${date}`)) next.delete(k);
        }
        return next;
      });
    }
    setOverriddenBHDates(prev => { const next = new Set(prev); next.delete(date); return next; });
    setPendingBHRestoreDate(null);
  }, [rowCodes, existingEntries, connector, calendarMonth]);

  // ── Request submit (validate → save → confirm modal) ─────────────────────────

  const handleSubmitRequest = useCallback(() => {
    if (!currentUser || !selectedAsset) return;
    if (rowCodes.filter(Boolean).length === 0) return;
    setShowSubmitConfirm(true);
  }, [currentUser, selectedAsset, rowCodes]);

  // ── Cancel / discard changes ────────────────────────────────────────────────
/*
  const handleCancel = useCallback(() => {
    monthDirtyValues.current.delete(calendarMonth);
    setDirtyValues(new Map());
    setRowCodes(prev => {
      const next = prev.filter(Boolean);
      monthRowCodes.current.set(calendarMonth, next);
      return next;
    });
  }, [calendarMonth]);
*/
  // ── Derived status ─────────────────────────────────────────────────────────

  const isDirty = dirtyValues.size > 0;
  const isRejected = useMemo(
    () => [...existingEntries.values()].some(e => e.status === TimesheetStatus.Rejected),
    [existingEntries],
  );
  const rejectionNote = useMemo(
    () => [...existingEntries.values()].find(e => e.rejectionNote)?.rejectionNote ?? '',
    [existingEntries],
  );
  const status = isSubmitted ? 'submitted' : isRejected ? 'rejected' : 'draft';
  const pillClass =
    status === 'draft' ? 'pill pill-draft' : status === 'rejected' ? 'pill pill-rejected' : 'pill pill-pending';
  const pillLabel = status === 'draft' ? 'Draft' : status === 'rejected' ? 'Rejected' : 'Pending Approval';

  // ── Submit button label (TS-13/TS-32): "Submit for Month" on last week ──────
  const submitLabel = isLastWeekOfMonth ? 'Submit for Month' : 'Submit for Approval';

  const monthDisplayLabel = (() => {
    const [y, m] = periodMonth.split('-');
    return `${new Date(+y, +m - 1, 1).toLocaleString('en-GB', { month: 'long' })} ${y}`;
  })();

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Topbar */}
      <div className="topbar">
        <div className="topbar-l">
          <div className="crumb">
            <span>Timesheets</span>
            <span className="sep">/</span>
            <span className="cur">Asset Timesheets</span>
          </div>
          <div className="wk-nav" style={{ position: 'relative' }}>
            <button onClick={goToPrevMonth} aria-label="Previous month" title="Previous month">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span
              className="wk-lbl-top wk-lbl-click"
              onClick={() => setShowWeekPicker(v => !v)}
              title="Click to select a week"
            >
              {monthLabel}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12 }}>
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </span>
            <button onClick={goToNextMonth} aria-label="Next month" title="Next month">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
            {showWeekPicker && (
              <div
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={() => setShowWeekPicker(false)}
              >
                <div
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, width: 300, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>Jump to Week</span>
                    <button onClick={() => setShowWeekPicker(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', fontSize: 18, lineHeight: 1 }}>✕</button>
                  </div>
                  {getCalendarWeeksForMonth(periodMonth).slice().reverse().map(w => (
                    <div
                      key={w.weekStart}
                      onClick={() => { goToWeek(w.weekStart); setShowWeekPicker(false); }}
                      style={{
                        padding: '10px 14px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 13.5,
                        marginBottom: 4,
                        border: '1px solid var(--border)',
                        background: w.weekStart === weekStart ? 'var(--accent)' : '',
                        color: w.weekStart === weekStart ? '#fff' : 'var(--text)',
                        transition: 'background 0.15s',
                      }}
                      onMouseOver={e => { if (w.weekStart !== weekStart) (e.currentTarget as HTMLDivElement).style.background = 'var(--surface2)'; }}
                      onMouseOut={e => { if (w.weekStart !== weekStart) (e.currentTarget as HTMLDivElement).style.background = ''; }}
                    >
                      {formatWeekLabel(w.weekStart)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="topbar-r" />
      </div>

      {/* Content */}
      <div className="content">
        {/* Asset selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            Asset:
          </label>
          {isLoading && !selectedAssetId ? (
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading assets…</span>
          ) : loadError ? (
            <span style={{ fontSize: 13, color: 'var(--red)' }}>{loadError}</span>
          ) : assets.length === 0 ? (
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>No active assets found</span>
          ) : (
            <select
              className="finput"
              value={selectedAssetId}
              onChange={e => setSelectedAssetId(e.target.value)}
              style={{ maxWidth: 360 }}
              disabled={isSaving}
            >
              {assets.map(a => (
                <option key={a.id} value={a.id}>
                  {a.title}{a.description ? ` — ${a.description}` : ''}
                </option>
              ))}
            </select>
          )}
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Log hours against an asset for R&amp;D capitalisation tracking
          </span>
        </div>

        {/* Error banner */}
        {saveError && (
          <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 6, fontSize: 13, color: 'var(--red)' }}>
            {saveError}
          </div>
        )}

        {/* Timesheet card */}
        <div className="ts-card">
          <div className="ts-card-hdr">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="ts-card-title">
                {assetLabel}&nbsp;Weekly Asset Timesheet
              </span>
              <div className='wk-nav'>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={goToPrevWeek}
                  disabled={isFirstWeekOfMonth}
                  title="Previous week"
                  style={{ padding: '2px 7px' }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}>
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <span
                  className='wk-lbl-top'
                >
                  {weekLabel}
                </span>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={goToNextWeek}
                  disabled={isLastWeekOfMonth}
                  title="Next week"
                  style={{ padding: '2px 7px' }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}>
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
              </div>
            <span className={pillClass}>{pillLabel}</span>
          </div>

          {isLoading ? (
            <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Loading…
            </div>
          ) : loadError ? (
            <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--red)', fontSize: 13 }}>
              {loadError}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
            <table className="ts-tbl">
              <thead>
                <tr>
                  <th className="tcol">Project / Task</th>
                  {dayHeaders.map((h, i) => {
                    const isBh = bankHolidayDates.has(h.isoDate);
                    const isOom = !isDateInMonth(h.isoDate, periodMonth);
                    return (
                      <th key={i} className={isOom ? 'col-oom' : h.isWeekend ? 'wknd-hdr' : ''}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                          {h.day}
                          {isBh && <span className="bh-dot" />}
                        </div>
                        <small style={{ display: 'block', fontWeight: 400, letterSpacing: 0, textTransform: 'none' }}>
                          {h.date}
                        </small>
                      </th>
                    );
                  })}
                  <th className="ttotal">Total</th>
                </tr>
              </thead>
              <tbody>
                {/* Bank holiday auto-rows */}
                {weekBankHolidays.map(bh => {
                  const projHours = rowCodes.filter(Boolean).reduce((s, code) => s + (getCellHours(code, bh.holidayDate) ?? 0), 0);
                  const remainingBH = Math.max(0, bh.defaultHours - projHours);
                  const hasWorkedHours = projHours > 0;
                  const isRestoreMode = overriddenBHDates.has(bh.holidayDate) || hasWorkedHours;
                  return (
                  <tr key={bh.holidayDate} className="bh-auto-row" style={{ background: 'rgba(217,119,6,0.05)' }}>
                    <td className="tcell">
                      <div className="task-name" style={{ color: 'var(--amber)' }}>
                        🏖 Bank Holiday
                        <span className="badge" style={{ background: 'rgba(217,119,6,0.15)', color: 'var(--amber)' }}>
                          Auto
                        </span>
                      </div>
                      <div className="task-meta">{bh.holidayName} — auto-inserted</div>
                    </td>
                    {dayHeaders.map((h, i) => {
                      const isWknd = i >= 5;
                      if (h.isoDate === bh.holidayDate) {
                        return (
                          <td key={i}>
                            <input
                              type="text"
                              className="hr-input bh"
                              value={remainingBH > 0 ? decimalToHHMM(remainingBH) : ''}
                              readOnly
                              placeholder="–"
                              style={
                                hasWorkedHours
                                  ? { color: 'var(--amber)', fontWeight: 600, opacity: 0.65 }
                                  : { color: 'var(--amber)', fontWeight: 600 }
                              }
                              title={
                                hasWorkedHours
                                  ? `Remaining bank holiday: ${decimalToHHMM(remainingBH)} (${decimalToHHMM(bh.defaultHours)} minus project hours logged)`
                                  : `Bank Holiday — ${decimalToHHMM(bh.defaultHours)} auto-inserted`
                              }
                            />
                            {!isSubmitted && (
                              <button
                                type="button"
                                onClick={() => {
                                  if (hasWorkedHours) {
                                    setPendingBHRestoreDate(bh.holidayDate as ISODateString);
                                  } else {
                                    setOverriddenBHDates(prev => {
                                      const next = new Set(prev);
                                      if (next.has(bh.holidayDate)) next.delete(bh.holidayDate);
                                      else next.add(bh.holidayDate);
                                      return next;
                                    });
                                  }
                                }}
                                title={isRestoreMode ? 'Restore full bank holiday hours' : 'I worked on this bank holiday'}
                                style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
                                  fontSize: 10, padding: '1px 5px', marginTop: 2,
                                  border: isRestoreMode ? '1px solid var(--border)' : '1.5px solid rgba(217,119,6,0.6)',
                                  borderRadius: 4,
                                  background: isRestoreMode ? 'transparent' : 'rgba(217,119,6,0.13)',
                                  color: isRestoreMode ? 'var(--text-muted)' : 'var(--amber)',
                                  cursor: 'pointer', width: '100%',
                                  fontWeight: isRestoreMode ? 400 : 700,
                                }}
                              >
                                {isRestoreMode ? <>↩ Restore</> : <>💼 Worked?</>}
                              </button>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={i} className={isWknd ? 'col-wknd' : ''}>
                          <input
                            type="text"
                            className={`hr-input bh${isWknd ? ' wknd' : ''}`}
                            value=""
                            readOnly
                            placeholder="–"
                          />
                        </td>
                      );
                    })}
                    <td className="total-cell" style={{ color: 'var(--amber)', opacity: hasWorkedHours ? 0.65 : 1 }}>
                      {remainingBH > 0 ? fmtHours(remainingBH) : '–'}
                    </td>
                  </tr>
                  );
                })}

                {/* Project rows */}
                {rowCodes.map((code, rowIdx) => {
                  if (!code) {
                    // Pending selection row
                    return (
                      <tr key={`pending-${rowIdx}`}>
                        <td className="tcell" colSpan={9}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <select
                              className="finput"
                              style={{ maxWidth: 320, fontSize: 13 }}
                              value=""
                              onChange={e => {
                                const selected = e.target.value;
                                if (!selected) return;
                                setRowCodes(prev => {
                                  const next = prev.map((c, i) => (i === rowIdx ? selected : c));
                                  monthRowCodes.current.set(calendarMonth, next.filter(Boolean));
                                  return next;
                                });
                              }}
                            >
                              <option value="">Select project…</option>
                              {availableProjects.map(p => (
                                <option key={p.id} value={p.projectCode}>
                                  {p.projectCode} — {p.projectName}
                                </option>
                              ))}
                            </select>
                            <button
                              className="btn btn-ghost"
                              style={{ fontSize: 12, padding: '2px 8px' }}
                              onClick={() => {
                                setRowCodes(prev => {
                                  const next = prev.filter((_, i) => i !== rowIdx);
                                  monthRowCodes.current.set(calendarMonth, next.filter(Boolean));
                                  return next;
                                });
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  const project = projectMap.get(code);
                  const rt = rowTotal(code);
                  const isRd = project?.isResearchDevelopment ?? false;
                  const projType = project?.type ?? ProjectType.Standard;
                  const canRemove = !isSubmitted && rt === 0;

                  return (
                    <tr key={code}>
                      <td className="tcell">
                        <div className="task-name" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>
                            {code}{' '}
                            {isRd && <span className="badge badge-rd">R&amp;D</span>}
                            {!isRd && projType === ProjectType.GA && <span className="badge badge-ga">G&amp;A</span>}
                            {!isRd && projType === ProjectType.Leave && <span className="badge badge-leave">Leave</span>}
                          </span>
                          {canRemove && (
                            <button
                              type="button"
                              title="Remove project row"
                              aria-label={`Remove ${code}`}
                              onClick={() => setPendingRemoveCode(code)}
                              style={{
                                border: 'none', background: 'transparent', cursor: 'pointer',
                                color: 'var(--text-dim)', fontSize: 14, lineHeight: 1,
                                padding: '0 4px',
                              }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        <div className="task-meta">{project?.projectName ?? ''}</div>
                      </td>
                      {dayHeaders.map((h, di) => {
                        const isWknd = di >= 5;
                        const isBh = bankHolidayDates.has(h.isoDate);
                        const isOom = !isDateInMonth(h.isoDate, periodMonth);
                        const hVal = getCellHours(code, h.isoDate);

                        if (isOom) {
                          return (
                            <td key={di} className="col-oom">
                              <input
                                className="hr-input oom"
                                type="number"
                                value=""
                                readOnly
                                placeholder="–"
                                title="Enter these hours on the adjacent month's timesheet"
                              />
                            </td>
                          );
                        }

                        const isBhLocked = isBh && !overriddenBHDates.has(h.isoDate) && !(hVal !== null && hVal > 0);
                        if (isBhLocked) {
                          return (
                            <td key={di}>
                              <input
                                className="hr-input bh"
                                type="text"
                                value=""
                                readOnly
                                placeholder="–"
                                title="Bank Holiday — click 'Worked?' on the BH row to enter hours"
                              />
                            </td>
                          );
                        }

                        return (
                          <td key={di}>
                            <AssetHoursCell
                              code={code}
                              isoDate={h.isoDate}
                              hVal={hVal}
                              isSubmitted={isSubmitted}
                              isWknd={isWknd}
                              onUpdate={updateHours}
                            />
                          </td>
                        );
                      })}
                      <td className="total-cell">{fmtHours(rt)}</td>
                    </tr>
                  );
                })}

                {/* Add row */}
                {!isSubmitted && (
                  <tr className="add-row">
                    <td colSpan={9}>
                      <button
                        className="add-row-btn"
                        disabled={availableProjects.length === 0}
                        onClick={() => setRowCodes(prev => [...prev, ''])}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Add project or task…
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="tfoot-row">
                  <td className="tfoot-lbl">Daily Total</td>
                  {dayHeaders.map((h, i) => {
                    const isBh = bankHolidayDates.has(h.isoDate);
                    const isOom = !isDateInMonth(h.isoDate, periodMonth);
                    const dt = dailyTotals[i];
                    if (isOom) {
                      return <td key={i} className="tfoot-day col-oom">–</td>;
                    }
                    if (isBh) {
                      const bhProjHours = rowCodes.filter(Boolean).reduce((s, code) => s + (getCellHours(code, h.isoDate) ?? 0), 0);
                      const bhRemaining = Math.max(0, bankHolidays.find(b => b.holidayDate === h.isoDate)?.defaultHours ?? 0) - bhProjHours;
                      const bhDisplayTotal = bhProjHours + Math.max(0, bhRemaining);
                      if (!overriddenBHDates.has(h.isoDate) && bhProjHours === 0) {
                        return (
                          <td key={i} className="tfoot-day" style={{ color: 'var(--amber)' }}>
                            {decimalToHHMM(bankHolidays.find(b => b.holidayDate === h.isoDate)?.defaultHours ?? 0)} bh
                          </td>
                        );
                      }
                      return (
                        <td key={i} className="tfoot-day">
                          {bhDisplayTotal > 0 ? decimalToHHMM(bhDisplayTotal) : '–'}
                        </td>
                      );
                    }
                    return (
                      <td key={i} className="tfoot-day">
                        {fmtHours(dt)}
                      </td>
                    );
                  })}
                  <td className="tfoot-grand">
                    {fmtHours(grandTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
            </div>
          )}

          {/* Rejection reason callout */}
          {isRejected && rejectionNote && (
            <div
              className="month-rule-note"
              style={{ margin: '12px 18px 12px 18px', background: 'var(--red-soft)', border: '1px solid rgba(220,38,38,0.25)', borderLeft: '3px solid var(--red)' }}
            >
              <svg className="mrn-ico" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <div>
                <strong style={{ color: 'var(--red)' }}>Rejected.</strong> {rejectionNote}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="ts-actions">
            <div className="ts-actions-l">
              <div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                  {weekLabel} — {pillLabel.toLowerCase()}
                </div>
              </div>
              <button className="btn btn-ghost">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export
              </button>
            </div>
            <div className="ts-actions-r">
              {lastSaved && <span className="saved-lbl">Saved {lastSaved}</span>}
              {(status === 'draft' || status === 'rejected') && !isLoading && (
                <>
                  <button
                    className={`btn${isDirty && !isSaving ? ' btn-save-dirty' : ' btn-ghost'}`}
                    disabled={isSaving || !isDirty}
                    onClick={() => void handleSave()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}>
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" />
                      <polyline points="7 3 7 8 15 8" />
                    </svg>
                    {isSaving ? 'Saving…' : 'Save Draft'}
                  </button>
                  <button
                    className="btn btn-success"
                    disabled={isSaving || rowCodes.filter(Boolean).length === 0}
                    onClick={handleSubmitRequest}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {submitLabel}
                  </button>
                </>
              )}
              {status === 'submitted' && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  {SOFT_LAUNCH.enabled ? 'Awaiting super approver' : 'Awaiting approval'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating save reminder */}
      {isDirty && !isSubmitted && (
        <button className="save-reminder" onClick={() => void handleSave()}>
          💾 Please press save!
        </button>
      )}

      {/* Remove row confirmation */}
      {pendingRemoveCode && (
        <div className="modal-backdrop" onClick={() => setPendingRemoveCode(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <p className="modal-title">Remove project row?</p>
            <p className="modal-subtitle">
              This will permanently delete all timesheet hour entries for <strong>{pendingRemoveCode}</strong> across the entire month of <strong>{monthDisplayLabel}</strong> from SharePoint. This cannot be undone.
            </p>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setPendingRemoveCode(null)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => confirmRemoveRow(pendingRemoveCode)}
              >
                Delete &amp; Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore bank holiday confirmation */}
      {pendingBHRestoreDate && (() => {
        const workedHours = rowCodes.filter(Boolean).reduce((s, code) => s + (getCellHours(code, pendingBHRestoreDate) ?? 0), 0);
        const bh = weekBankHolidays.find(b => b.holidayDate === pendingBHRestoreDate);
        return (
          <div className="modal-backdrop" onClick={() => setPendingBHRestoreDate(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <p className="modal-title">Restore bank holiday?</p>
              <p className="modal-subtitle">
                You have logged <strong>{decimalToHHMM(workedHours)}</strong> of project hours on this bank holiday.
                Restoring will delete those entries and reinstate the full <strong>{decimalToHHMM(bh?.defaultHours ?? VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS)}</strong> bank holiday allowance.
              </p>
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setPendingBHRestoreDate(null)}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => confirmBHRestore(pendingBHRestoreDate)}
                >
                  Delete &amp; Restore
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Submit confirmation */}
      {showSubmitConfirm && (
        <div className="modal-backdrop" onClick={() => setShowSubmitConfirm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <p className="modal-title">{isLastWeekOfMonth ? 'Submit for Month?' : 'Submit for Approval?'}</p>
            <p className="modal-subtitle">
              {isLastWeekOfMonth
                ? "You're on the last week of the month. Submitting will send the full month's asset timesheet for manager approval."
                : "Are you sure you want to submit this month's asset timesheet? Once submitted it will be sent for manager approval."}
            </p>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowSubmitConfirm(false)}>
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={() => { setShowSubmitConfirm(false); void handleSubmit(); }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
